import { useState } from 'react';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import { AppPassword } from '../../../../../types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  getAppPasswords,
  getUserQuery,
} from '../../../../api/users-api';
import DeleteAppPasswordDialog from './delete-app-password-dialog';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useParams } from 'react-router-dom';
import { Messages } from '../../../../utils/message-constants';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';

import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Delete from '@mui/icons-material/Delete';
import Password from '@mui/icons-material/Password';

export default function AppPasswords() {
  const { userEmail } = useParams() as { userEmail: string };
  const { userRoles, changeLogRoles } = useUserSettings();

  const { data: user } = useQuery(getUserQuery(userEmail));

  const queryClient = useQueryClient();
  const [openDeleteAppPassword, setOpenDeleteAppPassword] = useState(false);
  const [selectedAppPassword, setSelectedAppPassword] = useState<AppPassword>();

  const {
    data: appPasswords,
    isLoading,
    isFetching: appPasswordsFetching,
  } = useQuery({
    queryKey: [UserKeys.AppPasswords, userEmail],
    queryFn: () => getAppPasswords(userEmail),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('app passwords'),
    },
  });

  const columns: GridColDef<AppPassword>[] = [
    {
      field: 'name',
      headerName: 'Password Name',
      editable: false,
      sortable: false,
      filterable: false,
      flex: 1,
    },
    {
      field: 'creationTime',
      headerName: 'Created Date',
      editable: false,
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => {
        const date = new Date(params.value);
        return date.toLocaleString();
      },
    },
    {
      field: 'lastTimeUsed',
      headerName: 'Last Used',
      editable: false,
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => {
        if (params.value === 0) return 'Not yet used';
        const date = new Date(params.value);
        return date.toLocaleString();
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      flex: 1,
      getActions: (params) => {
        return [
          <GridActionsCellItem
            key={params.row.codeId}
            style={{
              display: isAbleToSeeUIElement(
                userRoles,
                UserRoles.DeleteAppPasswords.value
              )
                ? 'inherit'
                : 'none',
            }}
            label="Delete"
            onClick={() => {
              setSelectedAppPassword(params.row);
              setOpenDeleteAppPassword(true);
            }}
            icon={
              <Tooltip arrow title="Delete">
                <Delete />
              </Tooltip>
            }
          />,
        ];
      },
    },
  ];

  if (!user) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{ width: '100%', minHeight: 300 }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  function CustomToolbar() {
    return (
      <SharedToolbar
        noColumn
        noFilter
        noQuickFilter
        secondaryButtons={
          <SharedIconButton
            variant="refresh"
            onClick={() => {
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.AppPasswords, userEmail],
              });
            }}
            tooltipLabel="Refresh"
          />
        }
      />
    );
  }

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('App Passwords', <Password />),
        }}
        getRowId={(row) => row.codeId}
        rows={appPasswords || []}
        columns={columns}
        loading={isLoading || appPasswordsFetching}
        disableColumnSelector
        disableRowSelectionOnClick
        disableColumnMenu
      />
      {selectedAppPassword ? (
        <DeleteAppPasswordDialog
          open={openDeleteAppPassword}
          handleClose={() => setOpenDeleteAppPassword(false)}
          selectedAppPassword={selectedAppPassword}
          userEmail={userEmail}
          allAppPasswords={appPasswords || []}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
    </>
  );
}
